/**
 * CONNOX GMBH CONFIDENTIAL
 * ________________________
 *
 * Copyright (c) 2005 - 2020 Connox GmbH, All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Connox GmbH and its suppliers, if any. The intellectual and
 * technical concepts contained herein are proprietary to Connox GmbH and
 * its suppliers and may be covered by German Laws and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * Connox GmbH.
 */

import {IMoney} from "../../cart/Money";
import {IAddressResponseData} from "../../checkout/AddressResponseData";
import {BaseRequestHandler, IBaseRequestConfiguration} from "../../requestHandler/BaseRequestHandler";
import {getRequestConfigurationWithToken} from "../../requestHandler/getRequestConfigWithToken";
import {IVisitorData} from "../VisitorData";
import {IItemContent} from "../../items/interface/Item.interface";
import {IShopRating} from "../../ratings/RatingsFactory";
import {IReceiptData} from "../receipt/ReceiptData";
import timer from "../../logger/timer";

const baseURL: string = window.location.origin;

export class CustomerRequestHandler {
  /**
   * Creates or updates a customer via `POST` based on the two  mandatory fields `email`,
   * `newsletterFlag`, and other optional parameters. optIn mail process will be started if
   * mandatory `newsletterFlag` param is toggled to true. Note: if customerId is given,
   * you can also update various properties with a `.updateProperties(..)` method call
   * @param shopId Sales channel id
   * @param params Various params including mandatory fields: `email` and `newsletterFlag`
   * @returns Always returns `success: true` or throws an error
   */
  @timer("API", "Customer")
  public static async createOrUpdateCustomer(shopId: number, params: INewsletterPostParams): Promise<void> {
    const configuration = await getRequestConfigurationWithToken<INewsletterPostParams>();
    configuration.data = params;

    await BaseRequestHandler.performRequestNoResult<INewsletterPostParams>(
      "POST",
      `/shop/${shopId}/customers`,
      undefined,
      configuration,
    );
  }

  /**
   * Retrieves customer's properties by customerId and token
   * @param shopId sales channel id
   * @param customerId id of recognized or known customer
   */
  @timer("API", "Customer")
  public static async retrieveProperties(shopId: number, customerId: number): Promise<ICustomerPutParams> {
    const configuration = await getRequestConfigurationWithToken();

    return await BaseRequestHandler.performRequest<ICustomerPutParams>(
      "GET",
      `/shop/${shopId}/customers/${customerId}`,
      configuration,
    );
  }

  /**
   * Sets customer's properties by customerId and token. If customer
   * doesn't exist or is not recognised (no token/customerId == 0) method
   * `.createOrUpdateCustomer(<customersEmailAddress>)` has to be called
   * @param shopId sales channel id
   * @param customerId id of recognized or known customer
   * @param params
   */
  @timer("API", "Customer")
  public static async updateProperties(
    shopId: number,
    customerId: number,
    params: ICustomerPutParams,
  ): Promise<ICustomerPutParams> {
    const configuration = await getRequestConfigurationWithToken<ICustomerPutParams>();
    configuration.data = params;

    return await BaseRequestHandler.performRequest<ICustomerPutParams, ICustomerPutParams>(
      "PUT",
      `/shop/${shopId}/customers/${customerId}`,
      configuration,
    );
  }

  @timer("API", "Customer")
  public static async getAvailablePoints(shopId: number, customerId: number): Promise<IPoints[]> {
    const configuration = await getRequestConfigurationWithToken();

    return BaseRequestHandler.performRequest<IPoints[]>(
      "GET",
      `/shop/${shopId}/customers/${customerId}/points`,
      configuration,
    );
  }

  @timer("API", "Customer")
  public static async getLastAddresses(shopId: number, customerId: number): Promise<IAddressResponseData[]> {
    const configuration = await getRequestConfigurationWithToken();

    return BaseRequestHandler.performRequest("GET", `/shop/${shopId}/customers/${customerId}/address`, configuration);
  }

  /**
   * Sets customers address by api POST call
   * @param shopId sales channel id
   * @param customerId id of recognized or known customer
   * @param address address properties
   */
  @timer("API", "Customer")
  public static async postAddress(shopId: number, customerId: number, address: ICustomerAddress): Promise<void> {
    const configuration = await getRequestConfigurationWithToken<ICustomerAddress>();
    configuration.data = address;

    await BaseRequestHandler.performRequestNoResult<ICustomerAddress>(
      "POST",
      `/shop/${shopId}/customers/${customerId}/address`,
      undefined,
      configuration,
    );
  }

  @timer("API", "Customer")
  public static async getRatings(
    shopId: number,
    customerId: number,
    limit: number | null,
    offset: number | null,
    isRated: boolean | null,
  ): Promise<ICustomerRatingsResult[]> {
    const configuration = await getRequestConfigurationWithToken();
    configuration.params = {
      limit: limit?.toString(),
      offset: offset?.toString(),
      isRated: isRated?.valueOf(),
    };

    return BaseRequestHandler.performRequest<ICustomerRatingsResult[]>(
      "GET",
      `/shop/${shopId}/customers/${customerId}/ratings`,
      configuration,
    );
  }

  @timer("API", "Customer")
  public static async getReceipts(
    shopId: number,
    customerId: number,
    type: ReceiptType | null,
  ): Promise<IReceiptData[]> {
    const configuration = await getRequestConfigurationWithToken();
    configuration.params = {
      type: type?.toString(),
    };

    return BaseRequestHandler.performRequest<IReceiptData[]>(
      "GET",
      `/shop/${shopId}/customers/${customerId}/receipts`,
      configuration,
    );
  }

  @timer("API", "Customer")
  public static async getToken(): Promise<IVisitorData> {
    const configuration: IBaseRequestConfiguration = {
      baseURL,
    };

    return BaseRequestHandler.performRequest<IVisitorData>("GET", "/api/customer/jwt/token", configuration);
  }

  @timer("API", "Customer")
  public static async loginCustomer(credentials: ICredentials): Promise<IVisitorData | null> {
    const authString = btoa(`${credentials.username}:${credentials.password}`);
    const configuration: IBaseRequestConfiguration = {
      baseURL,
      headers: {Authorization: `Basic ${authString}`},
    };

    try {
      return await BaseRequestHandler.performRequest<IVisitorData>("GET", "/api/customer/login", configuration);
    } catch (e) {
      return null;
    }
  }

  @timer("API", "Customer")
  public static async logoutCustomer(): Promise<void> {
    try {
      // TODO: Try to find a more elegant way!
      await BaseRequestHandler.performRequestNoResult("GET", `${window.location.pathname}?Action=Logout`, baseURL);
    } catch (e) {
      throw new Error("An error occured while trying to log out\n" + e);
    }
  }

  /**
   * request magic link
   * @param email recipients email address
   * @param redirectUrl url placed in the email to redirect the user to
   */
  @timer("API", "Customer")
  public static async requestMagicLink(email: string, redirectUrl: string): Promise<void> {
    const data = new FormData();
    data.set("email", email);
    data.set("redirectUri", redirectUrl);
    const config: IBaseRequestConfiguration<FormData> = {data};
    await BaseRequestHandler.performRequestNoResult<FormData>("POST", "/api/magic/auth", baseURL, config);
  }

  @timer("API", "Customer")
  public static async requestPassword(email: string): Promise<void> {
    const data = new FormData();
    data.set("Email", email);
    const config: IBaseRequestConfiguration<FormData> = {data};
    await BaseRequestHandler.performRequestNoResult<FormData>("POST", "/api/customer/requestPassword", baseURL, config);
  }

  @timer("API", "Customer")
  public static async newsletterOptin(shopId: number, customerId: number): Promise<void> {
    const configuration = await getRequestConfigurationWithToken();

    await BaseRequestHandler.performRequestNoResult(
      "POST",
      `/shop/${shopId}/customers/${customerId}/newsletter-optin`,
      undefined,
      configuration,
    );
  }

  @timer("API", "Customer")
  public static async newsletterOptout(shopId: number, customerId: number): Promise<void> {
    const configuration = await getRequestConfigurationWithToken();

    await BaseRequestHandler.performRequestNoResult(
      "POST",
      `/shop/${shopId}/customers/${customerId}/newsletter-optout`,
      undefined,
      configuration,
    );
  }

  @timer("API", "Customer")
  public static async getNewsletterOptions(shopId: number, customerId: number): Promise<INewsletter[]> {
    const configuration = await getRequestConfigurationWithToken();

    return BaseRequestHandler.performRequest(
      "GET",
      `/shop/${shopId}/customers/${customerId}/newsletter-options`,
      configuration,
    );
  }

  @timer("API", "Customer")
  public static async setNewsletterOptions(
    shopId: number,
    customerId: number,
    newsletterData: INewsletter[],
  ): Promise<INewsletter[]> {
    const configuration = await getRequestConfigurationWithToken<INewsletter[]>();
    configuration.data = newsletterData;

    return BaseRequestHandler.performRequest(
      "PUT",
      `/shop/${shopId}/customers/${customerId}/newsletter-options`,
      configuration,
    );
  }
}

export interface ICredentials {
  password: string;
  username: string;
}

export interface INewsletterPostParams extends ICustomerParams {
  email: string;
  newsletterFlag: boolean;
  isCorporateCustomer?: boolean;
}

export interface ICustomerPutParams extends ICustomerParams {
  customerId?: number;
  email?: string | null;
  newsletterFlag?: boolean | null;
}

interface ICustomerParams {
  firstName?: string | null;
  lastName?: string | null;
  gender?: "M" | "F" | null;
  title?: string | null;
  birthday?: string | null;
}

export interface ICustomerAddress {
  addressId: string;
  city: string;
  countryCode: string;
  email: string;
  firstName: string;
  gender?: "M" | "F" | null;
  isLastDeliveryAddress: boolean;
  isLastInvoiceAddress: boolean;
  lastName: string;
  number: string;
  street: string;
  zip: string;
}

export interface ICustomerRatingsResult {
  item: IItemContent;
  rating?: IShopRating;
}

export interface IReceiptsResult {
  receiptData: IReceiptData[];
}

export enum ReceiptType {
  order = "order",
  invoice = "invoice",
  creditNote = "creditNote",
}

export interface IPoints {
  amount: IMoney;
  points: number;
}

export interface INewsletter {
  newsletterTypeId: number;
  newsletterTypeName?: string;
  newsletterTypeDescription?: string;
  isSubscribed: boolean;
}
